import { useState, useRef, useCallback, useMemo, ReactNode } from 'react'
import { GLOBAL_MODAL } from 'constants/DOM'
import { TABLE, FILTER } from 'texts/dropoffFee'
import { createPortal } from 'react-dom'
import { MODAL_WIDTH, OFFSET_ICON } from './constants'
import { useOutsideClick } from 'ui/hooks/useOutsideClick'
import { useDetectPositionChange } from 'ui/hooks/useDetectPositionChange'
import { Typography, OriginButton } from '@frontend/design_system'
import { SelectList } from 'ui/atoms/SelectList'
import { ResetIcon } from 'ui/icons/ResetIcon'
import { ACTIVITY_HISTORY } from 'texts/activityHistory'
import { joinClasses } from 'utils/joinClasses'
import { FilterIcon, ArrowLeftRightIcon } from 'ui/icons'
import { FilterRouteProps } from './types'
import styles from './styles.module.scss'
import {
  dropoffFilterDataSelector,
  isDropoffFilterAppliedSelector,
} from 'redux/dropoff/selectors'
import { useDispatch, useSelector } from 'react-redux'
import {
  setDropoffFilterData,
  setIsDropoffFilterApplied,
} from 'redux/dropoff/slice'

export const FilterRoute = ({
  items,
  onChange,
  isDisabled,
}: FilterRouteProps) => {
  const [isFilterListOpen, setFilterListOpen] = useState(false)
  const isDropoffFilterApplied = useSelector(isDropoffFilterAppliedSelector)
  const { pickUpLocation, dropOffLocation } = useSelector(
    dropoffFilterDataSelector
  )
  const filterIconRef = useRef<HTMLDivElement | null>(null)
  const [filterRef, setFilterRef] = useState<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const isDisabledFilter = isDisabled && !isDropoffFilterApplied

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setFilterListOpen((prev) => !prev)
  }, [])

  const itemsDropOffLocation = useMemo(() => {
    if (!items) {
      return []
    }
    return items.map((item) => ({
      label: item.name,
      value: item.id,
    }))
  }, [items])

  const filteredDropOffLocation = useMemo(() => {
    if (!pickUpLocation) return itemsDropOffLocation
    return itemsDropOffLocation.filter((item) => item.value !== pickUpLocation)
  }, [pickUpLocation, itemsDropOffLocation])

  const itemsPickUpLocation = useMemo(() => {
    if (!items) {
      return []
    }
    return items.map((item) => ({
      label: item.name,
      value: item.id,
    }))
  }, [items])

  const filteredPickUpLocation = useMemo(() => {
    if (!dropOffLocation) return itemsPickUpLocation
    return itemsPickUpLocation.filter((item) => item.value !== dropOffLocation)
  }, [dropOffLocation, itemsPickUpLocation])

  const handlePickUpOnChange = (value: string) => {
    dispatch(
      setDropoffFilterData({
        dropOffLocation,
        pickUpLocation: value,
      })
    )
  }

  const handleDropOffOnChange = (value: string) => {
    dispatch(
      setDropoffFilterData({
        dropOffLocation: value,
        pickUpLocation,
      })
    )
  }

  const handleCloseFilter = () => {
    setFilterListOpen(false)
  }

  const handleReset = () => {
    dispatch(setIsDropoffFilterApplied(false))
    dispatch(setDropoffFilterData({ pickUpLocation: '', dropOffLocation: '' }))
    onChange({ pickUpLocation: '', dropOffLocation: '' })
  }

  const handleFilterApply = () => {
    dispatch(setIsDropoffFilterApplied(true))
    onChange({ pickUpLocation, dropOffLocation })
    handleCloseFilter()
  }

  const renderDropdown = useCallback((children: ReactNode) => {
    if (filterIconRef.current) {
      const iconElement = filterIconRef.current.getBoundingClientRect()
      const { left, top, height } = iconElement

      return createPortal(
        <div
          style={{
            left: left - MODAL_WIDTH,
            top: top + OFFSET_ICON + height,
          }}
          className={styles['dropdown-wrapper']}
        >
          {children}
        </div>,
        document.getElementById(GLOBAL_MODAL) as HTMLElement
      )
    }
  }, [])

  useOutsideClick(filterRef, handleCloseFilter, {
    enabled: true,
    additionalElements: filterIconRef?.current ? [filterIconRef.current] : [],
  })
  useDetectPositionChange({ ref: filterIconRef, onChange: handleCloseFilter })

  return (
    <>
      <div
        className={styles['filter-icon-wrapper']}
        ref={filterIconRef}
        onClick={!isDisabledFilter ? handleClick : undefined}
        data-testid="filter-route"
      >
        <FilterIcon
          color={isDisabledFilter ? 'grey500' : 'blue700'}
          className={joinClasses(
            [styles['filter-active'], isFilterListOpen],
            [styles.disabled, isDisabledFilter]
          )}
          dataTestId="filter-icon"
        />
        {isDropoffFilterApplied && <div className={styles.selected} />}
      </div>

      {isFilterListOpen && (
        <>
          {renderDropdown(
            <div
              className={styles['filter-modal']}
              data-testid="filter-modal"
              ref={setFilterRef}
            >
              <div className={styles['filter-modal-header']}>
                <Typography
                  name="Subtitle3"
                  className={styles['header-first-title']}
                >
                  {TABLE.PICK_UP}
                </Typography>
                <ArrowLeftRightIcon size="medium" color="grey900" />
                <Typography
                  name="Subtitle3"
                  className={styles['header-second-title']}
                >
                  {TABLE.DROP_OFF}
                </Typography>
              </div>
              <div className={styles['filter-body']}>
                <div ref={listRef} className={styles['filter-left-side']}>
                  <SelectList
                    items={filteredPickUpLocation}
                    selectedValue={pickUpLocation}
                    onChange={handlePickUpOnChange}
                    containerRef={listRef}
                    search={{
                      placeholder: FILTER.SEARCH,
                      showSearch: true,
                    }}
                    notFoundTitle={FILTER.NOT_FOUND}
                    inputSize="medium"
                    dataTestId="pickup-location-select"
                  />
                </div>
                <div ref={listRef} className={styles['filter-right-side']}>
                  <SelectList
                    items={filteredDropOffLocation}
                    selectedValue={dropOffLocation}
                    onChange={handleDropOffOnChange}
                    containerRef={listRef}
                    search={{
                      placeholder: FILTER.SEARCH,
                      showSearch: true,
                    }}
                    notFoundTitle={FILTER.NOT_FOUND}
                    inputSize="medium"
                    dataTestId="dropoff-location-select"
                  />
                </div>
              </div>
              <div className={styles['filter-footer']}>
                <div>
                  {(dropOffLocation || pickUpLocation) && (
                    <Typography
                      Tag="div"
                      color="blue700"
                      name="Button2"
                      className={styles['reset-button']}
                      onClick={handleReset}
                      dataTestId="reset-button"
                    >
                      <div className={styles['reset-icon']}>
                        <ResetIcon color="blue700" size="small" />
                      </div>
                      {ACTIVITY_HISTORY.RESET}
                    </Typography>
                  )}
                </div>
                <div className={styles['filter-apply']}>
                  <OriginButton
                    onClick={handleFilterApply}
                    label={TABLE.APPLY}
                    disabled={!pickUpLocation && !dropOffLocation}
                    dataTestId="apply-button"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
